/**
 * The sidebar's nav list.
 *
 * The journal's `nav-main`, on this app's router. Two things change and
 * nothing else: `next/link` becomes react-router's `NavLink`, which gives us
 * the active state directly rather than by comparing pathnames, and the
 * pending-navigation spinner goes — this app's routes are in one bundle and
 * resolve in the same frame, so a spinner would only ever flash.
 */

import { NavLink, useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'

export interface NavItem {
  title: string
  url: string
  icon?: LucideIcon
  /** Short label for the collapsed rail's hover tooltip. Defaults to `title`. */
  shortTitle?: string
  testid?: string
}

export function NavMain({ items }: { items: readonly NavItem[] }) {
  const { pathname } = useLocation()
  const { isMobile, openMobile, setOpenMobile } = useSidebar()

  // On a phone the sidebar is a sheet over the content; leaving it open on
  // top of the page you just asked for is never what was meant.
  const closeOnMobile = (): void => {
    if (isMobile && openMobile) setOpenMobile(false)
  }

  return (
    <SidebarGroup>
      <SidebarMenu className="space-y-1">
        {items.map((item) => {
          // `end` on the terminal only: without it "/" matches every route
          // and the whole nav lights up at once.
          const isActive =
            item.url === '/' ? pathname === '/' : pathname.startsWith(item.url)

          return (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton
                asChild
                isActive={isActive}
                tooltip={{ children: item.shortTitle ?? item.title, sideOffset: 8 }}
              >
                <NavLink to={item.url} onClick={closeOnMobile} data-testid={item.testid}>
                  {item.icon ? <item.icon className="mr-2" /> : null}
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
